import { Link } from 'react-router-dom';

const useCases = [
  {
    icon: 'ri-cpu-line',
    title: 'NJ-Serie – Machine Controller',
    description:
      'Die OMRON NJ-Serie verbindet SPS-Steuerung mit Motion Control auf einer Plattform. Wir programmieren und integrieren NJ-Controller für hochdynamische Mehrachsanwendungen, Montageautomation und Prüfanlagen.',
    tags: ['NJ101', 'NJ301', 'NJ501', 'EtherCAT Motion'],
  },
  {
    icon: 'ri-server-line',
    title: 'NX-Serie – Modulare I/O & Safety',
    description:
      'Mit der NX-Plattform realisieren wir skalierbare Steuerungsarchitekturen – von kompakten NX1P2-Einheiten bis hin zu komplexen NX102-Systemen mit integrierter Sicherheitstechnik (Safety CPU).',
    tags: ['NX1P2', 'NX102', 'NX Safety', 'Modular I/O'],
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'Sysmac Studio – Engineering Software',
    description:
      'Alle OMRON-Projekte entwickeln wir in Sysmac Studio – der einheitlichen Engineering-Umgebung für SPS, Motion, HMI und Safety. Strukturierter Code nach IEC 61131-3, versioniert und dokumentiert.',
    tags: ['IEC 61131-3', 'Structured Text', 'Function Blocks', 'Simulation'],
  },
  {
    icon: 'ri-git-branch-line',
    title: 'EtherCAT & Netzwerkintegration',
    description:
      'Wir integrieren OMRON-Systeme nahtlos in bestehende Netzwerkinfrastrukturen. EtherCAT für Echtzeit-Motion, EtherNet/IP und OPC-UA für übergeordnete MES/SCADA-Anbindung.',
    tags: ['EtherCAT', 'EtherNet/IP', 'OPC-UA', 'Profinet'],
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Safety & Risikobeurteilung',
    description:
      'Sicherheitsfunktionen nach EN ISO 13849 und IEC 62061 – wir implementieren Safety-Logik direkt in der OMRON NX Safety CPU und erstellen die vollständige Sicherheitsdokumentation.',
    tags: ['PLd / PLe', 'SIL 2 / SIL 3', 'Safety CPU', 'EN ISO 13849'],
  },
  {
    icon: 'ri-remote-control-line',
    title: 'HMI & Visualisierung',
    description:
      'Intuitive Bedienoberflächen mit OMRON NA-Series HMI oder PC-basierter SCADA-Visualisierung. Klare Prozessdarstellung, Alarmmanagement und Fernzugriff für Wartung und Diagnose.',
    tags: ['NA-Series HMI', 'SCADA', 'Fernwartung', 'Alarmmanagement'],
  },
];

const highlights = [
  { value: 'NJ / NX', label: 'Sysmac Plattform' },
  { value: 'IEC 61131-3', label: 'Programmierstandard' },
  { value: 'EtherCAT', label: 'Echtzeit-Bus' },
  { value: 'Safety', label: 'Integrierte Sicherheit' },
];

const OmronSection = () => {
  return (
    <section className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#DC2626]"></span>
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Partner</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
              OMRON Sysmac –<br />
              <span className="text-[#1E3A8A]">Steuerungstechnik auf höchstem Niveau</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-6">
              Als OMRON Partner programmieren und integrieren wir OMRON Sysmac SPS-Systeme
              in komplexe Automatisierungsanlagen. Die Sysmac-Plattform vereint SPS, Motion Control,
              Safety und HMI in einer einheitlichen Engineering-Umgebung – für maximale Effizienz
              und Zuverlässigkeit in Ihrer Produktion.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Von der Projektplanung über die Programmierung in Sysmac Studio bis zur vollständigen
              Inbetriebnahme und Dokumentation – wir begleiten Ihr OMRON-Projekt von Anfang bis Ende.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {highlights.map((h) => (
                <div key={h.label} className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center min-w-[110px]">
                  <div className="text-[#1E3A8A] font-bold text-sm">{h.value}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{h.label}</div>
                </div>
              ))}
            </div>
            <Link
              to="/anfrage"
              className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1e40af] text-white font-semibold px-6 py-3 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              OMRON-Projekt anfragen
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>

          {/* Right: Logo + Image */}
          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center h-36">
              <img
                src="https://storage.readdy-site.link/project_files/0c485b57-fedb-40d8-8940-c50727e75c3c/9668503a-dcd7-40d6-8bc5-9e13acd0327c_Omron-logo.jpg?v=c9c44afe4a42e3aac7d26ed2abc7929b"
                alt="OMRON – Partner gowiTEC"
                className="max-h-full max-w-[260px] object-contain"
                loading="lazy"
              />
            </div>
            <div className="w-full h-[280px] rounded-xl overflow-hidden bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=OMRON%20industrial%20automation%20PLC%20control%20cabinet%20with%20NJ%20NX%20series%20controllers%20mounted%20in%20a%20professional%20electrical%20panel%2C%20clean%20wiring%20and%20modern%20industrial%20automation%20components%2C%20white%20and%20grey%20tones%2C%20professional%20manufacturing%20environment%20with%20precise%20cable%20management%20and%20terminal%20blocks&width=760&height=420&seq=omron-plc-panel-01&orientation=landscape"
                alt="OMRON NJ/NX Steuerungssystem – gowiTEC Integration"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-gray-400 text-xs font-semibold tracking-widest uppercase whitespace-nowrap">Konkrete Einsatzbereiche</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {useCases.map((uc) => (
            <div
              key={uc.title}
              className="bg-white rounded-xl border border-gray-100 p-7 flex flex-col gap-4 hover:border-[#1E3A8A]/30 transition-colors duration-200"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                <i className={`${uc.icon} text-[#1E3A8A] text-xl`}></i>
              </div>
              <div>
                <h3 className="text-gray-900 font-bold text-base mb-2">{uc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{uc.description}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-2">
                {uc.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-[#EFF6FF] text-[#1E3A8A] text-xs font-medium px-2.5 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div className="bg-[#0f1f4a] rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
              <i className="ri-award-line text-white text-2xl"></i>
            </div>
            <div>
              <div className="text-white font-bold text-lg mb-1">OMRON Partner</div>
              <div className="text-blue-200 text-sm leading-relaxed max-w-lg">
                Kompetenz für OMRON Sysmac NJ/NX – von der Planung über Sysmac Studio
                bis zur vollständigen Inbetriebnahme und Dokumentation.
              </div>
            </div>
          </div>
          <Link
            to="/anfrage?leistung=Steuerungstechnik%2C+SPS+%26+Automation"
            className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#b91c1c] text-white font-semibold px-6 py-3 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm shrink-0"
          >
            Jetzt anfragen
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default OmronSection;
